/*
 * Live allocation state for the §7 orders screens. The my-api holder is the
 * SOLE source of truth for who holds a seat (fix e75108d); box-office
 * seatStatus is never inherited. Unconfigured/unavailable ⇒ honest notice +
 * unknown-as-unassigned with actions disabled — nothing invented (ruling A).
 */
import { useCallback, useEffect, useState } from 'react'
import { loadSession } from '../../auth/session'
import { useData } from '../../data/DataProvider'
import { fetchOrderAllocations, myApiBase, MyApiError, type OrderAllocations } from '../../data/myApi'
import type { Order, Ticket } from '../../data/types'
import { applyAllocations } from './ordersModel'

/**
 * Honest allocation-readability states (D11, CMT-2026-08-09-043). These are
 * DISTINCT so the notice copy never blames the build for a merely absent
 * session:
 *   'unconfigured' — no VITE_MY_API_BASE (genuine build gap)
 *   'signed-out'   — no live session, or a 401/403 from my-api (auth)
 *   'unavailable'  — network / 5xx (service down)
 */
export type AllocState = 'unconfigured' | 'signed-out' | 'loading' | 'ready' | 'unavailable'

export interface OrderSeats {
  state: AllocState
  /** Tickets with my-api holders merged into ruling-B seats[]. */
  tickets: Ticket[]
  refresh: () => void
}

/** Allocation-merged seats for ONE order. */
export function useOrderSeats(order: Order | null): OrderSeats {
  const { ticketsForOrder, sessionEmail } = useData()
  const [state, setState] = useState<AllocState>('unconfigured')
  const [allocations, setAllocations] = useState<OrderAllocations | null>(null)
  const [tick, setTick] = useState(0)

  const orderId = order?.id ?? null
  useEffect(() => {
    const base = myApiBase()
    const session = loadSession()
    // Split the guard (D11): build gap ≠ signed-out ≠ nothing-to-load.
    if (!base) {
      setState('unconfigured')
      setAllocations(null)
      return
    }
    if (!session) {
      setState('signed-out')
      setAllocations(null)
      return
    }
    if (!orderId) {
      setState('unconfigured')
      setAllocations(null)
      return
    }
    let cancelled = false
    setState('loading')
    fetchOrderAllocations(session.idToken, base, orderId)
      .then((a) => {
        if (cancelled) return
        setAllocations(a)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setAllocations(null)
        // A 401/403 is an auth failure (expired/absent session), NOT the
        // service being down — route it to the sign-in notice (D11).
        const status = err instanceof MyApiError ? err.status : undefined
        setState(status === 401 || status === 403 ? 'signed-out' : 'unavailable')
      })
    return () => { cancelled = true }
  }, [orderId, tick])

  const refresh = useCallback(() => setTick((t) => t + 1), [])
  const base = orderId ? ticketsForOrder(orderId) : []
  return {
    state,
    tickets: applyAllocations(base, state === 'ready' ? allocations : null, sessionEmail),
    refresh,
  }
}
